import * as React from 'react'

import { cn } from '../../lib/utils'

interface FieldProps extends Omit<React.ComponentProps<'div'>, 'children'> {
  label?: React.ReactNode
  /** Helper text under the control, hidden while an error is showing. */
  hint?: React.ReactNode
  error?: React.ReactNode
  required?: boolean
  /** id of the control, wired to the label's htmlFor. */
  htmlFor?: string
  /** Rendered at the right of the label row (e.g. a "Forgot?" link). */
  labelExtra?: React.ReactNode
  children: React.ReactNode
}

/**
 * Label + control + hint/error, with no opinion about how the control is bound.
 *
 * Deliberately dependency-free: form binding lives one layer up in an adapter
 * (react-hook-form here, Inertia's useForm in roundhouse-rails), so this file
 * ports without dragging a form library along.
 */
function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  labelExtra,
  className,
  children,
  ...props
}: FieldProps) {
  return (
    <div data-slot="field" className={cn('w-full', className)} {...props}>
      {(label || labelExtra) && (
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          {label && (
            <label htmlFor={htmlFor} className="t-label">
              {label}
              {required && <span className="text-err ml-1">*</span>}
            </label>
          )}
          {labelExtra}
        </div>
      )}
      {children}
      {/* Stable ids so an adapter can point the control's aria-describedby at
          whichever of the two is showing; role="alert" so a validation message
          that appears after submit is announced rather than just drawn. */}
      {error ? (
        <p id={htmlFor && `${htmlFor}-error`} role="alert" className="text-err mt-1 text-xs">
          {error}
        </p>
      ) : hint ? (
        <p id={htmlFor && `${htmlFor}-hint`} className="text-fg-subtle mt-1 text-xs">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export { Field, type FieldProps }
